/**
 * Archive — every day so far, newest first.
 *
 * Past days are safe to show in full colour: the target swatch is right there,
 * which turns the list into a nice record of what the game has looked like.
 * Replaying one is for pride only and never touches the streak.
 */

import { h, ICON, svg } from '../dom';
import { ctx, onLeave } from '../ctx';
import { GRADES } from '../../game/grade';
import { loadResults } from '../../state/store';
import { swatch } from '../paint';
import { longDate, puzzleNumber, specForDay, todayIndex } from '../../core/schedule';
import { puzzleFor } from '../cache';

const PAGE = 40;

export function archiveScreen(): HTMLElement {
  const root = h('div', { class: 'screen archive' });
  const today = todayIndex();
  const results = new Map(loadResults().map((r) => [r.index, r]));
  let shown = PAGE;

  root.appendChild(
    h(
      'div',
      { class: 'bar' },
      h(
        'button',
        {
          class: 'icon',
          attrs: { type: 'button', 'aria-label': 'Back' },
          on: { click: () => ctx.go('home') },
        },
        svg(ICON.back),
      ),
      h('div', { class: 'bar__title', text: 'Archive' }),
      h('div', { class: 'bar__spacer' }),
      h('div', { class: 'caps', text: `${puzzleNumber(today)} puzzles` }),
    ),
  );

  const scroll = h('div', { class: 'scroll' });
  const list = h('div', { class: 'stack', style: { paddingBottom: 'var(--s-5)' } });
  const more = h('button', {
    class: 'btn btn--wide',
    attrs: { type: 'button' },
    text: 'Show older',
    on: {
      click: () => {
        shown += PAGE;
        render();
      },
    },
  });

  scroll.appendChild(list);
  scroll.appendChild(more);
  root.appendChild(scroll);

  function render(): void {
    const from = Math.max(0, today - shown + 1);
    const rows: HTMLElement[] = [];

    for (let index = today; index >= from; index--) {
      // Fetching the puzzle is what makes this list expensive, so it is done
      // lazily as pages are revealed rather than all at once.
      const puzzle = puzzleFor(index);
      const result = results.get(index);
      const isToday = index === today;

      rows.push(
        h(
          'button',
          {
            class: 'archive__item',
            attrs: { type: 'button' },
            on: {
              click: () => {
                if (isToday) {
                  if (result) ctx.go('reveal');
                  else ctx.start(index, 'daily');
                } else {
                  ctx.start(index, 'archive');
                }
              },
            },
          },
          h('div', { class: 'archive__swatch', style: swatch(puzzle.target.hex) }),
          h(
            'div',
            { class: 'archive__meta' },
            h('div', { class: 'archive__n', text: `#${puzzleNumber(index)} · ${specForDay(index).label}` }),
            h('div', { class: 'archive__sub', text: longDate(index) }),
          ),
          h('div', {
            class: 'archive__grade',
            style: result ? { '--grade': `var(--${GRADES[result.grade].token})` } : undefined,
            text: result ? GRADES[result.grade].label : isToday ? 'Today' : 'Play',
          }),
        ),
      );
    }

    list.replaceChildren(...rows);
    more.hidden = from === 0;
  }

  render();

  // The archive is the one screen long enough to be worth memoising a puzzle
  // for; `puzzleFor` keeps its own small cache.
  onLeave(() => {});

  return root;
}

export { specForDay };
